import { isListingExpired } from '#shared/listingExpiry';
import type { PublishedListing } from '#shared/types';

export const LISTING_SORTS = [
  'alphabetical',
  'datePosted',
  'category',
] as const;

export type ListingSort = (typeof LISTING_SORTS)[number];

export type ListingGroup = {
  heading: string;
  listings: PublishedListing[];
};

const UNCATEGORISED_HEADING = 'Other';

export const activeListings = (
  listings: PublishedListing[],
  now: Date = new Date(),
): PublishedListing[] =>
  listings.filter((listing) => !isListingExpired(listing.expiresAt, now));

export const uniqueServiceTypes = (listings: PublishedListing[]): string[] => {
  const types = new Set<string>();
  for (const listing of listings) {
    const type = listing.serviceType?.trim();
    if (type) {
      types.add(type);
    }
  }
  return [...types].sort((a, b) => a.localeCompare(b));
};

export const filterListings = (
  listings: PublishedListing[],
  serviceType: string,
  search: string,
): PublishedListing[] => {
  const query = search.trim().toLowerCase();
  return listings.filter((listing) => {
    const matchesType =
      !serviceType ||
      serviceType === 'All' ||
      listing.serviceType === serviceType;
    const haystack = [listing.name, listing.serviceType, listing.blurb]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesSearch = !query || haystack.includes(query);
    return matchesType && matchesSearch;
  });
};

const listingName = (listing: PublishedListing): string =>
  listing.name?.trim() ?? '';

const compareNames = (a: PublishedListing, b: PublishedListing): number => {
  const nameA = listingName(a);
  const nameB = listingName(b);
  if (!nameA && !nameB) {
    return 0;
  }
  if (!nameA) {
    return 1;
  }
  if (!nameB) {
    return -1;
  }
  return nameA.localeCompare(nameB, 'en', { sensitivity: 'base' });
};

const postedAtTime = (listing: PublishedListing): number | undefined => {
  if (!listing.postedAt) {
    return undefined;
  }
  const time = Date.parse(listing.postedAt);
  return Number.isFinite(time) ? time : undefined;
};

const comparePostedAt = (a: PublishedListing, b: PublishedListing): number => {
  const timeA = postedAtTime(a);
  const timeB = postedAtTime(b);
  if (timeA === undefined && timeB === undefined) {
    return compareNames(a, b);
  }
  if (timeA === undefined) {
    return 1;
  }
  if (timeB === undefined) {
    return -1;
  }
  return timeB - timeA;
};

export const sortListings = (
  listings: PublishedListing[],
  sort: ListingSort,
): PublishedListing[] => {
  const copy = [...listings];
  if (sort === 'datePosted') {
    return copy.sort(comparePostedAt);
  }
  return copy.sort(compareNames);
};

export const groupListingsByCategory = (
  listings: PublishedListing[],
): ListingGroup[] => {
  const groups = new Map<string, PublishedListing[]>();
  const uncategorised: PublishedListing[] = [];

  for (const listing of listings) {
    const type = listing.serviceType?.trim();
    if (!type) {
      uncategorised.push(listing);
      continue;
    }
    const existing = groups.get(type);
    if (existing) {
      existing.push(listing);
    } else {
      groups.set(type, [listing]);
    }
  }

  const sortedGroups: ListingGroup[] = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
    .map(([heading, groupListings]) => ({
      heading,
      listings: [...groupListings].sort(compareNames),
    }));

  if (uncategorised.length > 0) {
    sortedGroups.push({
      heading: UNCATEGORISED_HEADING,
      listings: [...uncategorised].sort(compareNames),
    });
  }

  return sortedGroups;
};
